import { Loader2 } from "lucide-react"
import { useEffect, useRef, useState, type ReactNode } from "react"
import { TabDirtyDot } from "@/components/ui/tab-dirty-dot"
import { floatingTabIdsWithHiddenLabels, type FloatingTabLabelCollapseTab } from "@/lib/floating-tab-label-collapse"
import { UI_ACCENT_FILLED } from "@/lib/ui-selection"
import { cn } from "@/lib/utils"

/** Floating pill shell — position it with `absolute`/`fixed` from the caller. */
export const FLOATING_TAB_PILL_CLASS =
  "inline-flex flex-nowrap items-center gap-1 rounded-lg border border-ui-border bg-ui-surface p-0.5 backdrop-blur-md dark:bg-zinc-950/95 light:bg-ui-bg-elevated/95"
export const FLOATING_TAB_BUTTON_CLASS =
  "relative inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium whitespace-nowrap text-ui-text-secondary"
const FLOATING_TAB_BUTTON_ICON_ONLY_CLASS =
  "relative inline-flex shrink-0 items-center justify-center rounded-lg px-1.5 py-1 text-xs font-medium whitespace-nowrap text-ui-text-secondary"

export type FloatingTabPillTab = {
  id: string
  label: string
  active: boolean
  dirty?: boolean
  /** When false and inactive: muted icon+label. Filled/active/undefined: normal. */
  filled?: boolean
  /** Label may hide under overflow (icon stays). Omit on tabs that must keep text. */
  labelCollapsible?: boolean
  /**
   * When true, icon shows only after the label is collapsed (canvas).
   * Default / omit: icon always visible alongside the label (shot preview).
   */
  iconWhenCollapsedOnly?: boolean
  icon?: ReactNode
  /** Во вкладке идёт процесс (генерация, подготовка): вместо иконки крутится спиннер. */
  busy?: boolean
  onClick: () => void
}

type FloatingTabPillDirtyTone = "danger" | "approved"

type FloatingTabPillProps = {
  tabs: FloatingTabPillTab[]
  className?: string
  /** aria-label applied to each dirty dot (e.g. "Unseen changes"). */
  dirtyAriaLabel?: string
  /** Dot color: `danger` (default, unsaved) or `approved` (unread after generation). */
  dirtyTone?: FloatingTabPillDirtyTone
}

function tabButtonClass(tab: FloatingTabPillTab, labelHidden: boolean) {
  return cn(
    labelHidden ? FLOATING_TAB_BUTTON_ICON_ONLY_CLASS : FLOATING_TAB_BUTTON_CLASS,
    tab.active ? UI_ACCENT_FILLED : "hover:text-ui-text-primary",
    tab.filled === false && !tab.active && "text-ui-text-muted",
  )
}

function TabButtonContent({
  tab,
  labelHidden,
  dirtyAriaLabel,
  dirtyTone,
}: {
  tab: FloatingTabPillTab
  labelHidden: boolean
  dirtyAriaLabel?: string
  dirtyTone?: FloatingTabPillDirtyTone
}) {
  const showIcon = Boolean(tab.icon) && (labelHidden || !tab.iconWhenCollapsedOnly)
  return (
    <>
      {tab.busy ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden /> : showIcon ? tab.icon : null}
      {labelHidden ? null : tab.label}
      {tab.dirty ? <TabDirtyDot ariaLabel={dirtyAriaLabel} tone={dirtyTone} /> : null}
    </>
  )
}

/** Shared floating tab switcher (shot preview, canvas generation, workspace assistant dock). */
export function FloatingTabPill({ tabs, className, dirtyAriaLabel, dirtyTone }: FloatingTabPillProps) {
  const collapseEnabled = tabs.some((tab) => tab.labelCollapsible === true)
  const containerRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const [hiddenLabelIds, setHiddenLabelIds] = useState<ReadonlySet<string>>(() => new Set())
  const measureKey = tabs
    .map(
      (tab) =>
        `${tab.id}:${tab.label}:${tab.labelCollapsible ? 1 : 0}:${tab.iconWhenCollapsedOnly ? 1 : 0}:${tab.active ? 1 : 0}:${tab.filled === false ? 0 : 1}:${tab.dirty ? 1 : 0}:${tab.busy ? 1 : 0}`,
    )
    .join("|")

  useEffect(() => {
    if (!collapseEnabled) {
      setHiddenLabelIds(new Set())
      return
    }

    const container = containerRef.current
    const measure = measureRef.current
    if (!container || !measure || typeof ResizeObserver === "undefined") return

    const recompute = () => {
      const labeledNodes = Array.from(measure.querySelectorAll<HTMLElement>("[data-tab-measure='labeled']"))
      const iconNodes = Array.from(measure.querySelectorAll<HTMLElement>("[data-tab-measure='icon']"))
      const iconById = new Map(iconNodes.map((el) => [el.dataset.tabId ?? "", el.offsetWidth]))

      const measured: FloatingTabLabelCollapseTab[] = tabs.map((tab, index) => {
        const labeledEl = labeledNodes[index]
        const labeledWidth = labeledEl?.offsetWidth ?? 0
        const iconOnlyWidth = tab.labelCollapsible ? (iconById.get(tab.id) ?? labeledWidth) : labeledWidth
        return {
          id: tab.id,
          labeledWidth,
          iconOnlyWidth,
          labelCollapsible: tab.labelCollapsible === true,
        }
      })

      setHiddenLabelIds(floatingTabIdsWithHiddenLabels(measured, container.clientWidth))
    }

    recompute()
    const observer = new ResizeObserver(recompute)
    observer.observe(container)
    observer.observe(measure)
    return () => observer.disconnect()
    // tabs captured for widths; measureKey tracks label/active/filled identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapseEnabled, measureKey])

  const pill = (
    <div className={cn(FLOATING_TAB_PILL_CLASS, collapseEnabled && "max-w-full", className)}>
      {tabs.map((tab) => {
        const labelHidden = hiddenLabelIds.has(tab.id)
        return (
          <button
            key={tab.id}
            type="button"
            aria-pressed={tab.active}
            aria-busy={tab.busy || undefined}
            aria-label={labelHidden ? tab.label : undefined}
            title={labelHidden ? tab.label : undefined}
            onClick={tab.onClick}
            className={tabButtonClass(tab, labelHidden)}
          >
            <TabButtonContent
              tab={tab}
              labelHidden={labelHidden}
              dirtyAriaLabel={dirtyAriaLabel}
              dirtyTone={dirtyTone}
            />
          </button>
        )
      })}
    </div>
  )

  if (!collapseEnabled) return pill

  return (
    <div ref={containerRef} className="flex w-full max-w-full justify-center">
      <div
        ref={measureRef}
        className="pointer-events-none fixed top-0 left-0 -z-50 flex items-center gap-1 p-0.5 opacity-0"
        aria-hidden
      >
        {tabs.map((tab) => (
          <button
            key={`labeled-${tab.id}`}
            type="button"
            tabIndex={-1}
            data-tab-measure="labeled"
            data-tab-id={tab.id}
            className={tabButtonClass(tab, false)}
          >
            <TabButtonContent tab={tab} labelHidden={false} dirtyAriaLabel={dirtyAriaLabel} dirtyTone={dirtyTone} />
          </button>
        ))}
        {tabs
          .filter((tab) => tab.labelCollapsible)
          .map((tab) => (
            <button
              key={`icon-${tab.id}`}
              type="button"
              tabIndex={-1}
              data-tab-measure="icon"
              data-tab-id={tab.id}
              className={tabButtonClass(tab, true)}
            >
              <TabButtonContent tab={tab} labelHidden dirtyAriaLabel={dirtyAriaLabel} dirtyTone={dirtyTone} />
            </button>
          ))}
      </div>
      {pill}
    </div>
  )
}
